import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import type { QueryObserverResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from '@/components/ui/use-toast';
import { useFeatureFlag } from '@/lib/featureFlags/useFeatureFlag';
import { AgentAuthorityContext } from '@/lib/layout/AgentAuthorityContext';
import { ChangeExpirationDateForm } from '@/lib/layout/forms/changeExpirationDate';
import { EndorsementForm } from '@/lib/layout/forms/endorsement';
import { ERPForm } from '@/lib/layout/forms/ERPForm';
import { ReinstateForm } from '@/lib/layout/forms/reinstate';
import { Refer } from '@/lib/layout/Header/components/DropdownComponents/Refer';
import { RatingErrorsContext } from '@/lib/layout/RatingErrorsContext';
import { userRoleEnum } from '@/lib/layout/types';
import { usePermissionsStore } from '@/lib/stores/usePermissionsStore';
import { useRole } from '@/lib/stores/useRole';
import { flattenAuthorityErrors } from '@/lib/utils/agentAuthorityNotifications';
import { useCurrentUserData } from '@/lib/utils/hooks';

import {
  glacierPolicyApiConfirmRatingChecksMutation,
  glacierPolicyApiCreateRenewalMutation,
  glacierPolicyApiGetAgentAuthorityQueryKey,
  glacierPolicyApiGetDentistQueryKey,
  glacierPolicyApiGetFormulasQueryKey,
  glacierPolicyApiGetLatestTransactionLatestAttemptQueryKey,
  glacierPolicyApiGetPolicyPermissionsQueryKey,
  glacierPolicyApiGetPolicyQueryKey,
  glacierPolicyApiGetSpecialFormsMutation,
  glacierPolicyApiListDocumentsQueryKey,
  glacierPolicyApiPricePreviewMidtermChangeQueryKey,
  glacierPolicyApiRatePolicyMutation,
  glacierPolicyApiRenewalSummaryOfChangesOptions,
  glacierPolicyFormsApiGetAvailableEndorsementsQueryKey,
  glacierPolicyFormsApiGetDentistAttachedFormsQueryKey,
  glacierPolicyFormsApiGetDentistAvailableEndorsementsQueryKey,
  glacierPolicyFormsApiGetEntityAttachedFormsQueryKey,
  glacierPolicyFormsApiGetEntityAvailableEndorsementsQueryKey,
  glacierPolicyFormsApiGetLocationAttachedFormsQueryKey,
  glacierPolicyFormsApiGetLocationAvailableEndorsementsQueryKey,
  glacierPolicyFormsApiPolicyLevelAttachedEndorsementsQueryKey,
} from '@/lib/heyapi/@tanstack/react-query.gen';
import type {
  AgentAuthorityResponse,
  ChildPolicyData,
  GlacierPolicyApiGetLatestTransactionLatestAttemptResponse,
  Policy,
  TransactionSummaryData,
} from '@/lib/heyapi/types.gen';
import {
  PolicyTypeEnum,
  TransactionStatus,
  TransactionType,
  UserRoleType,
} from '@/lib/heyapi/types.gen';

import { useAgentAuthorityErrors } from '../../AgentAuthorityErrorsContext';
import { formatErrorMessage, isPolicyValidParentForRenewal } from '../utils';
import AgentAuthorityConfirmDialog from './DropdownComponents/AgentAuthorityConfirmDialog';
import BypassValidationDialog from './DropdownComponents/BypassValidationDialog';
import { CancelPolicyDialog } from './DropdownComponents/CancelPolicy';
import { CopyQuote } from './DropdownComponents/CopyQuote';
import { DebugTransaction } from './DropdownComponents/DebugTransaction';
import { Decline } from './DropdownComponents/Decline';
import { Discard } from './DropdownComponents/Discard';
import { ErrorDialog } from './DropdownComponents/ErrorDialog';
import IssuePolicyDialog from './DropdownComponents/IssuePolicyDialog';
import LockPolicyDialog from './DropdownComponents/LockPolicyDialog';
import { CloseRenewal } from './DropdownComponents/RenewalOptions/CloseRenew';
import { FlagForManualRenew } from './DropdownComponents/RenewalOptions/FlagForManualRenew';
import { MarkAsNonRenew } from './DropdownComponents/RenewalOptions/MarkAsNonRenew';
import { MarkAsRenewable } from './DropdownComponents/RenewalOptions/MarkAsRenewable';
import { RemoveManualReviewFlag } from './DropdownComponents/RenewalOptions/RemoveManualReviewFlag';
import { Reopen } from './DropdownComponents/Reopen';
import ReopenUnlockedAccountDialog from './DropdownComponents/ReopenUnlockedAccountDialog';
import { Transfer } from './DropdownComponents/Transfer';
import type { Message } from './StandardMessage';

export const DropdownMenuButton = ({
  renewalPolicyData,
  policy,
  latestTransactionSummary,
  latestRateAttempt,
  setMessage,
}: {
  renewalPolicyData: ChildPolicyData | null;
  policy: Policy | undefined;
  latestTransactionSummary?: TransactionSummaryData;
  latestRateAttempt?: GlacierPolicyApiGetLatestTransactionLatestAttemptResponse;
  setMessage: (message: Message | undefined) => void;
}) => {
  const queryClient = useQueryClient();
  const { agentAuthorityErrors, setAgentAuthorityErrors } =
    useAgentAuthorityErrors();
  const { setErrors } = useContext(RatingErrorsContext);
  const navigate = useNavigate();
  const role = usePermissionsStore((state) => state.role);
  const { hasRoleOrAbove } = useRole();
  const {
    editMode,
    permissionsAllowEdit,
    agentAuthority,
    refetchAgentAuthority,
  } = useContext(AgentAuthorityContext);
  const { id, dentistId, locationId, entityId } = useParams();
  const [loading, setLoading] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [bypassing, setBypassing] = useState(false);
  const [forming, setForming] = useState(false);
  const [loadingDebug, setLoadingDebug] = useState(false);
  const [dialogContent, setDialogContent] = useState<
    | 'RATING'
    | 'TRANSFER'
    | 'REFER'
    | 'DISCARD'
    | 'DECLINE'
    | 'NOT_TAKEN'
    | 'REOPEN'
    | 'MARK_AS_NON_RENEW'
    | 'FLAG_FOR_MANUAL_REVIEW'
    | undefined
  >(undefined);

  // Check if policy is a renewal that hasn't been discarded or cancelled
  // Valid when: policy type is RENEWAL AND (status is not DISCARDED OR transaction type is not POLICY_CANCELLED)
  const isValidRenewalPolicy =
    policy?.details.policyType === PolicyTypeEnum.RENEWAL &&
    policy.latestTransaction.status !== TransactionStatus.DISCARDED &&
    policy.latestTransaction.transactionType !==
      TransactionType.POLICY_CANCELLED;

  // DC-3151: a bound renewal is itself a valid parent for the next renewal.
  // The previous version of this predicate excluded RENEWAL-type policies,
  // which hid "Create Renewal" from underwriters on every bound renewal.
  const isValidParentPolicy = isPolicyValidParentForRenewal(policy);

  const hasARenewalPolicy = Boolean(
    renewalPolicyData && renewalPolicyData.policyId !== null
  );

  const renewalPolicyHasBeenBound = Boolean(
    renewalPolicyData && renewalPolicyData.isBound === true
  );

  const { currentUser } = useCurrentUserData();
  const userAgencies = currentUser?.data?.agentData || [];
  const policyAgencyId = policy?.details.agency;
  const canRefer = userAgencies.some(
    (agency) => agency.canRefer && agency.agencyId === policyAgencyId
  );

  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [cancelPolicyDialogOpen, setCancelPolicyDialogOpen] = useState(false);
  const [flagForManualReviewDialogOpen, setFlagForManualReviewDialogOpen] =
    useState(false);
  const [markAsNonRenewDialogOpen, setMarkAsNonRenewDialogOpen] =
    useState(false);
  const [markAsRenewableDialogOpen, setMarkAsRenewableDialogOpen] =
    useState(false);
  const [
    removeManualReviewFlagDialogOpen,
    setRemoveManualReviewFlagDialogOpen,
  ] = useState(false);
  const [closeRenewalDialogOpen, setCloseRenewalDialogOpen] = useState(false);
  const [copyQuoteDialogOpen, setCopyQuoteDialogOpen] = useState(false);
  const [lockPolicyDialogOpen, setLockPolicyDialogOpen] = useState(false);
  const [issuePolicyDialogOpen, setIssuePolicyDialogOpen] = useState(false);
  const [agentAuthorityConfirmDialogOpen, setAgentAuthorityConfirmDialogOpen] =
    useState(false);
  const [bypassValidationDialogOpen, setBypassValidationDialogOpen] =
    useState(false);
  const bannerTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showAgentAuthorityErrors = useFeatureFlag(
    'ENABLE_AGENT_AUTHORITY_ERRORS'
  );
  const showRenewalFeatures = useFeatureFlag('ENABLE_RENEWAL_FEATURES');
  // Rollout switch (DC-3396): the backend evaluates the same waffle switch
  // and 404s the copy endpoints when it's off.
  const showCopyQuote = useFeatureFlag('ENABLE_COPY_QUOTE');

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (bannerTimeoutRef.current) {
        clearTimeout(bannerTimeoutRef.current);
      }
    };
  }, []);

  const qkReasonForChange = glacierPolicyApiPricePreviewMidtermChangeQueryKey({
    path: {
      account_id: id!,
    },
  });

  const policyQK = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: id!,
    },
  });

  const agentAuthorityQK = glacierPolicyApiGetAgentAuthorityQueryKey({
    path: { account_id: id! },
  });

  const warehouseKey = glacierPolicyApiListDocumentsQueryKey({
    path: { account_id: id! },
  });

  const formulasQK = glacierPolicyApiGetFormulasQueryKey({
    path: {
      account_id: id!,
    },
  });

  const permissionsQK = glacierPolicyApiGetPolicyPermissionsQueryKey({
    path: {
      account_id: id!,
    },
  });

  const dentistQK = glacierPolicyApiGetDentistQueryKey({
    path: {
      account_id: id!,
      dentist_id: dentistId || '',
    },
  });

  const policyLevelAttachedFQK =
    glacierPolicyFormsApiPolicyLevelAttachedEndorsementsQueryKey({
      path: {
        account_id: id!,
      },
    });

  const policyLevelAvailableFQK =
    glacierPolicyFormsApiGetAvailableEndorsementsQueryKey({
      path: {
        account_id: id!,
      },
    });

  const getDentistAttachedFQK =
    glacierPolicyFormsApiGetDentistAttachedFormsQueryKey({
      path: {
        account_id: id!,
        dentist_id: dentistId!,
      },
    });

  const getDentistAvailableFQK =
    glacierPolicyFormsApiGetDentistAvailableEndorsementsQueryKey({
      path: {
        dentist_id: dentistId!,
        account_id: id!,
      },
    });

  const getEntityAttachedFQK =
    glacierPolicyFormsApiGetEntityAttachedFormsQueryKey({
      path: {
        entity_id: entityId!,
        account_id: id!,
      },
    });

  const getEntityAvailableFQK =
    glacierPolicyFormsApiGetEntityAvailableEndorsementsQueryKey({
      path: {
        entity_id: entityId!,
        account_id: id!,
      },
    });

  const locationAttachedFQK =
    glacierPolicyFormsApiGetLocationAttachedFormsQueryKey({
      path: {
        location_id: locationId!,
        account_id: id!,
      },
    });

  const locationAvailableFQK =
    glacierPolicyFormsApiGetLocationAvailableEndorsementsQueryKey({
      path: {
        location_id: locationId!,
        account_id: id!,
      },
    });

  const latestRatingAttemptFQK =
    glacierPolicyApiGetLatestTransactionLatestAttemptQueryKey({
      path: {
        account_id: id!,
      },
    });

  const quoteForm = useMutation({
    ...glacierPolicyApiGetSpecialFormsMutation(),
    onSuccess() {
      setForming(false);
      setIssuing(false);
      queryClient.refetchQueries({ queryKey: warehouseKey });

      // Clear any existing timeout and show banner for 10 seconds
      if (bannerTimeoutRef.current) {
        clearTimeout(bannerTimeoutRef.current);
      }
      bannerTimeoutRef.current = setTimeout(() => {
        setMessage(undefined);
      }, 6000);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- backend error shape needs ApiError type (future ticket)
    onError(error: any) {
      setForming(false);
      setIssuing(false);

      if (error.status === 422) {
        setErrors(error.errors || null);

        toast({
          title: 'Error',
          description: (
            <div className="mt-2 w-[340px] rounded-md bg-red-300 p-4">
              <p className="text-white">
                Some required data is missing, please{' '}
                <Link
                  to={`/policy/${id}/policy-decisions?tab=ratingErrorMessages`}
                  className="underline"
                >
                  click here
                </Link>{' '}
                to review
              </p>
            </div>
          ),
        });

        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('ratingError', 'true');

        navigate('?' + searchParams.toString());
      } else if (error.status === 423) {
        // Freshest evaluation we have: write it even when empty so stale alerts clear.
        queryClient.setQueryData(agentAuthorityQK, error);
        if (flattenAuthorityErrors(error).length > 0) {
          setMessage({
            content: (
              <p className="inline-block">
                Please note, Agent Authority alerts. Please click{' '}
                <Link
                  to={`/policy/${id}/policy-decisions?tab=agentAuthorityErrors`}
                  className="underline"
                >
                  here
                </Link>{' '}
                to review
              </p>
            ),
            level: 'ERROR',
          });
        }
      } else {
        setMessage({
          content: 'Error generating the Quote Proposal',
          level: 'ERROR',
        });
      }
    },
  });

  useEffect(() => {
    if (policy?.bypassValidators) {
      setMessage({
        content: 'Data Validation is disabled for the current transaction.',
        level: 'INFO',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: review deps in future refactor
  }, [policy?.bypassValidators]);

  const getAgentAuthorityErrors = async () => {
    if (!refetchAgentAuthority) {
      return;
    }

    const { data: agentAuthorityData } =
      (await refetchAgentAuthority()) as unknown as QueryObserverResult<
        AgentAuthorityResponse,
        Error
      >;

    if (agentAuthorityData?.authorityErrors) {
      const errors = flattenAuthorityErrors(agentAuthorityData);
      if (errors.length) {
        setAgentAuthorityErrors(errors);
        toast({
          title: 'Agent Authority Errors',
          description: (
            <div className="mt-2 w-[340px] rounded-md bg-red-500 p-4">
              <p className="text-white">
                {errors.length} Agent Authority Errors found. Please{' '}
                <Link
                  to={`/policy/${id}/policy-decisions?tab=agentAuthorityErrors`}
                  className="underline"
                >
                  click here
                </Link>{' '}
                to review
              </p>
            </div>
          ),
        });
      } else {
        setAgentAuthorityErrors([]);
      }
    }
    queryClient.invalidateQueries({ queryKey: qkReasonForChange });
  };

  const rate = useMutation({
    ...glacierPolicyApiRatePolicyMutation(),
    onSuccess: async (data) => {
      queryClient.setQueryData(latestRatingAttemptFQK, data);
      queryClient.refetchQueries({ queryKey: policyQK });
      queryClient.refetchQueries({ queryKey: formulasQK });
      queryClient.refetchQueries({ queryKey: dentistQK });
      queryClient.refetchQueries({
        queryKey: [{ _id: 'glacierPolicyApiGetObjectDetails' }],
        exact: false,
        type: 'all',
      });

      queryClient.refetchQueries({ queryKey: policyLevelAttachedFQK });
      queryClient.refetchQueries({ queryKey: policyLevelAvailableFQK });
      queryClient.refetchQueries({ queryKey: getDentistAttachedFQK });
      queryClient.refetchQueries({ queryKey: getDentistAvailableFQK });
      queryClient.refetchQueries({ queryKey: getEntityAttachedFQK });
      queryClient.refetchQueries({ queryKey: getEntityAvailableFQK });
      queryClient.refetchQueries({ queryKey: locationAttachedFQK });
      queryClient.refetchQueries({ queryKey: locationAvailableFQK });

      setLoading(false);

      // Handle null response - rating failed silently
      if (!data) {
        setMessage({
          content:
            'Rating failed - no response received from server. Please try again or contact support.',
          level: 'ERROR',
        });
        return;
      }

      // Handle any error state from the rating response
      if (data.isError) {
        const request = data.request as
          | {
              policyDetails?: {
                transactionConfirmedRatingRuntimeChecks?: boolean;
              };
            }
          | undefined;

        if (!request?.policyDetails?.transactionConfirmedRatingRuntimeChecks) {
          setMessage({
            content:
              'Needs Additional Rating Checks: Please contact support to Review',
            level: 'ERROR',
          });
        } else {
          setMessage({
            content:
              'An error occurred while rating the policy. Please try again or contact support.',
            level: 'ERROR',
          });
        }
        return;
      }

      // Success case - only when data exists and isError is false
      setMessage({
        content: 'Rated successfully.',
        level: 'SUCCESS',
      });
      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">Account Rated</code>
          </pre>
        ),
      });

      setErrors([]);

      if (showAgentAuthorityErrors) {
        getAgentAuthorityErrors();
      } else {
        setTimeout(() => {
          window.location.reload();
        }, 800);
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- backend error shape needs ApiError type (future ticket)
    onError(error: any) {
      setLoading(false);

      try {
        let messageContent = 'Error while rating the policy.';
        queryClient.invalidateQueries({ queryKey: policyQK });
        queryClient.invalidateQueries({ queryKey: formulasQK });

        if (error?.status_message) {
          messageContent = `Unable to Rate: ${error.status_message}`;
        }
        setMessage({
          content: messageContent,
          level: 'ERROR',
        });
        if (error?.status === 422) {
          setErrors(error.errors || null);

          toast({
            title: 'Error',
            description: (
              <div className="mt-2 w-[340px] rounded-md bg-red-300 p-4">
                <p className="text-white">
                  Some required data is missing, please{' '}
                  <Link
                    to={`/policy/${id}/policy-decisions?tab=ratingErrorMessages`}
                    className="underline"
                  >
                    click here
                  </Link>{' '}
                  to review
                </p>
              </div>
            ),
          });

          const searchParams = new URLSearchParams(window.location.search);
          searchParams.set('ratingError', 'true');

          navigate('?' + searchParams.toString());
        } else if (error?.status === 423) {
          // Freshest evaluation we have: write it even when empty so stale alerts clear.
          queryClient.setQueryData(agentAuthorityQK, error);
          if (flattenAuthorityErrors(error).length > 0) {
            setMessage({
              content: (
                <p className="inline-block">
                  Please note, Agent Authority alerts. Please click{' '}
                  <Link
                    to={`/policy/${id}/policy-decisions?tab=agentAuthorityErrors`}
                    className="underline"
                  >
                    here
                  </Link>{' '}
                  to review
                </p>
              ),
              level: 'ERROR',
            });
          }
        } else if (typeof error?.status === 'number' && error.status >= 500) {
          setMessage({
            content: (
              <ErrorDialog
                title="Something went wrong while rating"
                onClose={() => setMessage(undefined)}
              >
                {formatErrorMessage({
                  message:
                    'An unexpected error occurred while rating the policy. Please try again or contact support.',
                })}
              </ErrorDialog>
            ),
            level: 'ERROR',
          });
        } else {
          let title = 'Missing fields required for rating';

          if (error?.detail?.includes('Error(')) {
            title = error.detail.split(':')[0];
          }

          const content = formatErrorMessage(error);

          setMessage({
            content: (
              <ErrorDialog title={title} onClose={() => setMessage(undefined)}>
                {content}
              </ErrorDialog>
            ),
            level: 'ERROR',
          });
        }
      } catch {
        // Catch-all: ensure we always show an error message to the user
        setMessage({
          content:
            'An error occurred while rating the policy. Please try again or contact support.',
          level: 'ERROR',
        });
      }
    },
  });
  function rateApplication() {
    setMessage({
      content: 'Application is being rated. Please wait...',
      level: 'INFO',
    });
    rate.mutate({ path: { account_id: policy!.pk! } });
  }

  const confirmRateChecks = useMutation({
    ...glacierPolicyApiConfirmRatingChecksMutation(),
    onMutate() {
      setMessage({
        content: 'Confirming runtime checks are acceptable. Please wait...',
        level: 'INFO',
      });
    },
    onSuccess() {
      rateApplication();
    },
    onError() {
      setMessage({
        content: 'Error confirming rating checks.',
        level: 'ERROR',
      });
    },
  });

  const { mutate: createRenewal } = useMutation({
    ...glacierPolicyApiCreateRenewalMutation(),
    onSuccess(data) {
      const renewalPolicyCreated = data as Policy;
      setMessage(undefined);
      queryClient.removeQueries({ queryKey: policyQK });

      toast({
        title: 'Renewal Created',
        description: (
          <div>
            <p>Renewal created successfully.</p>
          </div>
        ),
      });

      setTimeout(() => {
        window.location.href = `/policy/${renewalPolicyCreated.pk}/policy-details`;
      }, 700);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- backend error shape needs ApiError type (future ticket)
    onError(error: any) {
      setMessage(undefined);
      toast({
        title: 'Error Creating Renewal',
        description: formatErrorMessage(
          error,
          'An error occurred while creating the renewal'
        ),
      });
    },
  });

  const { refetch: getRenewalSummaryOfChanges } = useQuery({
    ...glacierPolicyApiRenewalSummaryOfChangesOptions({
      path: {
        account_id: id!,
      },
    }),
    enabled: false,
  });

  const handleDownloadRenewalSummaryOfChanges = async () => {
    setMessage({
      content: 'Downloading changes...',
      level: 'INFO',
    });
    const { status, error, data } = await getRenewalSummaryOfChanges();
    if (status === 'error') {
      const genericErrorMessage =
        'An error occurred while downloading the changes';
      const errorDetail = String(
        (error as unknown as Record<string, unknown>)?.detail ??
          error?.message ??
          ''
      );
      setMessage(undefined);
      toast({
        title: 'Error Downloading Changes',
        description: (
          <div>
            <p>{errorDetail || genericErrorMessage}</p>
          </div>
        ),
      });
    } else if (status === 'success') {
      setMessage(undefined);

      if (data) {
        const blob = new Blob([data as BlobPart], {
          type: 'application/pdf',
        });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');

        toast({
          title: 'Changes Downloaded',
          description: (
            <div>
              <p>Changes downloaded successfully</p>
            </div>
          ),
        });
      } else {
        const genericErrorMessage =
          'No changes found to download for this policy';
        setMessage(undefined);
        toast({
          title: 'No Changes Found',
          description: (
            <div>
              <p>{genericErrorMessage}</p>
            </div>
          ),
        });
      }
    }
  };

  const policyBoundButTransactionInProgress = useMemo(() => {
    // Used to determine if the policy is bound but not locked
    // Will remove once we lock all accounts and launch midterms
    if (
      policy &&
      policy?.latestTransaction.status === TransactionStatus.IN_PROGRESS &&
      policy?.boundPolicyNumber &&
      policy?.latestTransaction.transactionType === TransactionType.POLICY_BOUND
    ) {
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: review deps in future refactor
  }, [policy?.latestTransaction.status, policy?.boundPolicyNumber]);

  const declinable = useMemo(() => {
    return policy?.latestTransaction.status === TransactionStatus.IN_PROGRESS;
  }, [policy?.latestTransaction]);

  const discardable = useMemo(() => {
    const emptyBoundPolicyNumber = !policy?.boundPolicyNumber;

    return (
      emptyBoundPolicyNumber &&
      policy?.latestTransaction.status !== TransactionStatus.DISCARDED
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: review deps in future refactor
  }, [policy, role]);

  const reopenable = useMemo(() => {
    return (
      policy?.latestTransaction.status === TransactionStatus.DECLINED ||
      policy?.latestTransaction.status === TransactionStatus.NOT_TAKEN
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: review deps in future refactor
  }, [policy?.latestTransaction, role]);

  // IF (
  //     current_transaction.transaction_status == `ACTIVE`
  //     AND today is < policy.expiration_date
  // ):
  // THEN Show the Midterms menu
  const ratingModalCSS =
    'w-full max-w-7xl overflow-auto [&>button]:right-2 [&>button]:top-2';
  const modalCSS = 'w-full overflow-auto [&>button]:right-8 [&>button]:top-8';
  const transferModalCSS =
    'max-w-xl overflow-auto [&>button]:right-8 [&>button]:top-8';

  const policyFlaggedForNonRenewal = policy?.flaggedForNonRenewal || false;
  const policyFlaggedForManualReview = policy?.flaggedForManualRenewal || false;

  const isUnderwriterOrAbove = hasRoleOrAbove(UserRoleType.UNDERWRITER);

  const isPolicyActiveOrInProgress =
    policy?.latestTransaction.status === TransactionStatus.ACTIVE ||
    policy?.latestTransaction.status === TransactionStatus.IN_PROGRESS;

  // Actions visible only to Underwriter+
  const canCloseRenewal =
    isValidParentPolicy &&
    hasARenewalPolicy &&
    !renewalPolicyHasBeenBound &&
    isUnderwriterOrAbove;

  const canCreateRenewal =
    isValidParentPolicy &&
    !hasARenewalPolicy &&
    !policyFlaggedForNonRenewal &&
    isUnderwriterOrAbove;

  const canMarkAsNonRenew =
    !hasARenewalPolicy &&
    !policyFlaggedForNonRenewal &&
    isUnderwriterOrAbove &&
    isPolicyActiveOrInProgress;

  const canMarkAsRenewable =
    policyFlaggedForNonRenewal &&
    isUnderwriterOrAbove &&
    isPolicyActiveOrInProgress;

  const canFlagForManualReview =
    !hasARenewalPolicy &&
    !policyFlaggedForManualReview &&
    !policyFlaggedForNonRenewal &&
    isUnderwriterOrAbove &&
    isPolicyActiveOrInProgress;

  const canRemoveManualReviewFlag =
    policyFlaggedForManualReview &&
    isUnderwriterOrAbove &&
    isPolicyActiveOrInProgress;

  // Actions visible to all users
  const canSeeChangesFromParent =
    isValidRenewalPolicy && !policyFlaggedForNonRenewal;

  const canSeeChangesFromRenewal =
    isValidParentPolicy && !policyFlaggedForNonRenewal && hasARenewalPolicy;

  const canGoToParentAccount = isValidRenewalPolicy && policy?.parentPolicyId;

  const canGoToRenewalAccount = isValidParentPolicy && hasARenewalPolicy;

  // Show renewal section only if at least one action is visible
  const hasAnyRenewalAction =
    canCloseRenewal ||
    canCreateRenewal ||
    canMarkAsNonRenew ||
    canFlagForManualReview ||
    canRemoveManualReviewFlag ||
    canSeeChangesFromParent ||
    canSeeChangesFromRenewal ||
    canMarkAsRenewable ||
    canGoToParentAccount ||
    canGoToRenewalAccount;

  const allowRating = useMemo(() => {
    // Allow Rating IF
    // 1. The user has permission to edit (raw permission gate, not the
    //    combined editMode — re-rating must stay available during a
    //    Change Expiration Date midterm even though the rest of the
    //    policy is locked read-only).
    // 2. OR The Transaction is IN_PROGRESS AND User is System Admin
    if (!policy) {
      return false;
    }

    if (permissionsAllowEdit) {
      return true;
    }

    // This will allow System Admins to rate on All Midterms
    // Even if we are not in edit mode
    if (
      policy.latestTransaction.status === TransactionStatus.IN_PROGRESS &&
      role === userRoleEnum.SYSTEM_ADMIN
    ) {
      return true;
    }

    return false;
  }, [permissionsAllowEdit, policy, role]);

  // Midterm available actions from backend
  const showMidterms =
    latestTransactionSummary?.availableActions?.includes(
      'show_midterm_actions'
    ) || false;

  const allowEndorsementMidterm =
    latestTransactionSummary?.availableActions?.includes(
      'start_endorsement_midterm'
    ) || false;
  const allowCancelMidterm =
    latestTransactionSummary?.availableActions?.includes(
      'start_cancellation_midterm'
    ) || false;
  const allowReinstateMidterm =
    latestTransactionSummary?.availableActions?.includes(
      'start_reinstatement_midterm'
    ) || false;
  const allowChangeExpirationDateMidterm =
    latestTransactionSummary?.availableActions?.includes(
      'start_change_expiration_date_midterm'
    ) || false;
  const enableERP =
    latestTransactionSummary?.availableActions?.includes('start_erp') || false;

  if (loading) {
    <div className="flex flex-row-reverse items-center gap-4">
      <span className="text-sm">Rating</span>
      <LoaderCircle className="h-10 w-10 animate-spin text-brand-200" />
    </div>;
  }

  const disabledGenerateQuote = issuing || forming || !editMode;

  const referModalCSS =
    'max-w-xl overflow-auto [&>button]:right-4 [&>button]:top-4';

  let dialogClassName = modalCSS;
  if (dialogContent === 'RATING') {
    dialogClassName = ratingModalCSS;
  } else if (dialogContent === 'TRANSFER') {
    dialogClassName = transferModalCSS;
  } else if (dialogContent === 'REFER') {
    dialogClassName = referModalCSS;
  }

  const isPolicyAssignedToMe =
    policy?.assignedTo?.userId === currentUser?.data?.id;
  const isPolicyReferredToMe =
    isPolicyAssignedToMe && policy?.referredBy !== null;

  return (
    <>
      <Dialog>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              className="h-10 w-10 bg-[none] p-0 hover:bg-brand-200"
            >
              <DotsHorizontalIcon className="!hover:bg-[none] h-4 w-4 rounded-none text-[black]" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="max-h-[450px] overflow-y-auto"
          >
            <DropdownMenuItem onClick={() => {}} disabled>
              <span className="ml-2">Approve</span>
            </DropdownMenuItem>
            {role !== userRoleEnum.AGENT && declinable ? (
              <DialogTrigger
                asChild
                onClick={() => setDialogContent('DECLINE')}
              >
                <DropdownMenuItem disabled={!editMode}>
                  <span className="ml-2">Decline</span>
                </DropdownMenuItem>
              </DialogTrigger>
            ) : null}
            {role !== userRoleEnum.AGENT && reopenable ? (
              <DialogTrigger asChild onClick={() => setDialogContent('REOPEN')}>
                <DropdownMenuItem>
                  <span className="ml-2">Reopen</span>
                </DropdownMenuItem>
              </DialogTrigger>
            ) : null}

            {discardable ? (
              <DialogTrigger
                asChild
                onClick={() => setDialogContent('DISCARD')}
              >
                <DropdownMenuItem>
                  <span className="ml-2">Discard</span>
                </DropdownMenuItem>
              </DialogTrigger>
            ) : null}

            {role === userRoleEnum.AGENT && declinable ? (
              <DialogTrigger
                asChild
                onClick={() => setDialogContent('NOT_TAKEN')}
              >
                <DropdownMenuItem disabled={!editMode}>
                  <span className="ml-2">Not Taken</span>
                </DropdownMenuItem>
              </DialogTrigger>
            ) : null}

            <DialogTrigger asChild onClick={() => setDialogContent('TRANSFER')}>
              <DropdownMenuItem>
                <span className="ml-2">Transfer</span>
              </DropdownMenuItem>
            </DialogTrigger>

            {role === userRoleEnum.AGENT && canRefer ? (
              <DialogTrigger asChild onClick={() => setDialogContent('REFER')}>
                <DropdownMenuItem>
                  <span className="ml-2">Refer</span>
                </DropdownMenuItem>
              </DialogTrigger>
            ) : null}

            {role &&
            showCopyQuote &&
            // Copy Quote is for new business quotes only (product decision,
            // DC-3690): renewals and issued policies are ineligible, so the
            // action is hidden for them (the backend rejects both with a 422).
            // Mirror the backend guard, which keys off `is_issued` -- an issued
            // policy can have a blank bound number (e.g. backoffice clones of
            // legacy-numbered sources), so boundPolicyNumber alone would miss it.
            !policy?.parentPolicyId &&
            !policy?.isIssued &&
            !policy?.boundPolicyNumber ? (
              <DropdownMenuItem onClick={() => setCopyQuoteDialogOpen(true)}>
                <span className="ml-2">Copy Quote</span>
              </DropdownMenuItem>
            ) : null}

            <DropdownMenuItem
              onClick={() => {
                setForming(true);
                quoteForm.mutate({
                  path: { account_id: policy!.pk! },
                  query: { special_form: 'quote_proposal' },
                });
                setMessage({
                  content:
                    'Quote Proposal and Policy Specimen are being generated. Check Document Warehouse.',
                  level: 'INFO',
                });
              }}
              disabled={disabledGenerateQuote}
            >
              <span className="ml-2">
                {issuing || forming ? 'Generating Quote...' : 'Generate Quote'}
              </span>
            </DropdownMenuItem>

            {role === userRoleEnum.SYSTEM_ADMIN ? (
              <DropdownMenuItem
                onClick={() => {
                  setLoading(true);

                  confirmRateChecks.mutate({
                    path: { account_id: policy!.pk! },
                  });
                }}
                disabled={
                  confirmRateChecks.isPending ||
                  !latestRateAttempt?.isError ||
                  policy?.latestTransaction.confirmedRatingRuntimeChecks
                }
              >
                <span className="ml-2">
                  {confirmRateChecks.isPending
                    ? 'Confirming...'
                    : 'Confirm Rating Runtime Checks Acceptable'}
                </span>
              </DropdownMenuItem>
            ) : null}

            {policy?.latestTransaction.transactionType ===
            TransactionType.POLICY_BOUND ? (
              <DropdownMenuItem
                onClick={() => {
                  // Check if there are agent authority errors
                  if (
                    agentAuthorityErrors &&
                    agentAuthorityErrors.length > 0 &&
                    hasRoleOrAbove(UserRoleType.UNDERWRITER) &&
                    showAgentAuthorityErrors &&
                    isPolicyReferredToMe
                  ) {
                    setAgentAuthorityConfirmDialogOpen(true);
                  } else {
                    setIssuePolicyDialogOpen(true);
                  }
                }}
                disabled={issuing || !editMode}
              >
                <span className="ml-2">
                  {issuing ? 'Issuing Policy...' : 'Issue Policy'}
                </span>
              </DropdownMenuItem>
            ) : null}

            <DropdownMenuItem
              onClick={() => {
                setLoading(true);

                rateApplication();
              }}
              disabled={!allowRating}
            >
              <span className="ml-2">Rate</span>
            </DropdownMenuItem>

            {hasRoleOrAbove(UserRoleType.UNDERWRITING_ADMIN) ? (
              <DropdownMenuItem
                onClick={() => setBypassValidationDialogOpen(true)}
                disabled={
                  issuing ||
                  !editMode ||
                  (role === userRoleEnum.AGENT &&
                    agentAuthority?.issuePolicy === false)
                }
              >
                <span className="ml-2">
                  {policy?.bypassValidators
                    ? 'Enable Data Validation'
                    : 'Disable Data Validation'}
                </span>
              </DropdownMenuItem>
            ) : null}

            {role !== userRoleEnum.AGENT ? (
              <DialogTrigger
                asChild
                onClick={() => {
                  setDialogContent('RATING');
                  setLoadingDebug(true);
                }}
              >
                <DropdownMenuItem
                  disabled={
                    (role !== userRoleEnum.SYSTEM_ADMIN && editMode) ||
                    loadingDebug
                  }
                >
                  <span className="ml-2 flex items-center gap-4">
                    {loadingDebug ? (
                      <span className="flex items-center">
                        Fetching Previous Rating...
                        <LoaderCircle className="ml-2 h-4 w-4 animate-spin text-brand-200" />
                      </span>
                    ) : (
                      <span>Debug Previous Rating</span>
                    )}
                  </span>
                </DropdownMenuItem>
              </DialogTrigger>
            ) : null}

            {/* Midterms */}
            {/* 
                Buttons will only show if
                - current transaction is active
                AND
                - today is < policy expiration date
            */}
            {showMidterms || enableERP ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Midterm Actions</DropdownMenuLabel>

                {/* 

                    Endorsement Midterm Button

                    Show if the policy is not cancelled

                */}
                {allowEndorsementMidterm && showMidterms ? (
                  <Popover modal>
                    <PopoverTrigger asChild className="z-10">
                      <Button
                        className="flex w-full justify-start border-0 bg-none outline-none"
                        variant="ghost"
                      >
                        <span className="ml-2">Endorsement</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[500px] p-8">
                      <div>
                        <EndorsementForm policy={policy} />
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : null}

                {enableERP ? <ERPForm /> : null}

                {/*
                
                    Cancel Midterm Button

                    Show if the policy is not cancelled
                
                */}
                {allowCancelMidterm && showMidterms ? (
                  <Button
                    className="flex w-full justify-start border-0 bg-none outline-none"
                    variant="ghost"
                    onClick={() => setCancelPolicyDialogOpen(true)}
                  >
                    <span className="ml-2">Cancel</span>
                  </Button>
                ) : null}

                {/* 

                    Reinstate Midterm Button

                    Show if the policy is cancelled

                */}
                {allowReinstateMidterm && showMidterms ? (
                  <Popover modal>
                    <PopoverTrigger asChild className="z-10">
                      <Button
                        className="flex w-full justify-start border-0 bg-none outline-none"
                        variant="ghost"
                      >
                        <span className="ml-2">Reinstate</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[500px] p-8">
                      <div>
                        <ReinstateForm policy={policy} />
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : null}

                {/*

                    Change Expiration Date Midterm Button

                    Extend or shorten the policy term; creates a specialized
                    endorsement transaction. Hidden when a bound renewal
                    exists on this policy.

                */}
                {allowChangeExpirationDateMidterm && showMidterms ? (
                  <Popover modal>
                    <PopoverTrigger asChild className="z-10">
                      <Button
                        className="flex w-full justify-start border-0 bg-none outline-none"
                        variant="ghost"
                      >
                        <span className="ml-2">Change Expiration Date</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[500px] p-8">
                      <div>
                        <ChangeExpirationDateForm policy={policy} />
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : null}
              </>
            ) : null}
            {role === userRoleEnum.SYSTEM_ADMIN &&
            policyBoundButTransactionInProgress ? (
              <>
                <DropdownMenuItem
                  onClick={() => setLockPolicyDialogOpen(true)}
                  disabled={loading}
                >
                  <span className="ml-2 text-red-600">
                    {loading
                      ? 'Locking Policy'
                      : '***LOCK / FINALIZE POLICY***'}
                  </span>
                </DropdownMenuItem>

                {/* Button for re-open unbilled account */}
                <DropdownMenuItem onClick={() => setReopenDialogOpen(true)}>
                  <span className="ml-2 text-orange-600">
                    Re-Open Unbilled Account
                  </span>
                </DropdownMenuItem>
              </>
            ) : null}

            {showRenewalFeatures && hasAnyRenewalAction ? (
              <>
                {/* Renewal Actions */}
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Renewal Actions</DropdownMenuLabel>

                {canCloseRenewal ? (
                  <Button
                    className="flex w-full justify-start border-0 bg-none outline-none"
                    variant="ghost"
                    onClick={() => setCloseRenewalDialogOpen(true)}
                  >
                    <span className="ml-2">Close Renewal</span>
                  </Button>
                ) : null}
                {canCreateRenewal ? (
                  <Button
                    className="flex w-full justify-start border-0 bg-none outline-none"
                    variant="ghost"
                    onClick={() => {
                      setMessage({
                        content: 'Creating renewal...',
                        level: 'INFO',
                      });
                      createRenewal({
                        path: { account_id: policy!.pk! },
                      });
                    }}
                  >
                    <span className="ml-2">Create Renewal</span>
                  </Button>
                ) : null}
                {canSeeChangesFromParent ? (
                  <Button
                    className="flex w-full justify-start border-0 bg-none outline-none"
                    variant="ghost"
                    onClick={handleDownloadRenewalSummaryOfChanges}
                  >
                    <span className="ml-2">
                      {`See Changes from `}
                      <b>Prior Policy</b>
                    </span>
                  </Button>
                ) : null}
                {canSeeChangesFromRenewal ? (
                  <Button
                    className="flex w-full justify-start border-0 bg-none outline-none"
                    variant="ghost"
                    onClick={handleDownloadRenewalSummaryOfChanges}
                  >
                    <span className="ml-2">
                      {`See Changes from `}
                      <b>Renewal Policy</b>
                    </span>
                  </Button>
                ) : null}
                {canMarkAsNonRenew ? (
                  <Button
                    className="flex w-full justify-start border-0 bg-none outline-none"
                    variant="ghost"
                    onClick={() => {
                      setMarkAsNonRenewDialogOpen(true);
                    }}
                  >
                    <span className="ml-2">Mark as Non Renew</span>
                  </Button>
                ) : null}
                {canMarkAsRenewable ? (
                  <Button
                    className="flex w-full justify-start border-0 bg-none outline-none"
                    variant="ghost"
                    onClick={() => {
                      setMarkAsRenewableDialogOpen(true);
                    }}
                  >
                    <span className="ml-2">Mark as Renewable</span>
                  </Button>
                ) : null}

                {canFlagForManualReview ? (
                  <Button
                    className="flex w-full justify-start border-0 bg-none outline-none"
                    variant="ghost"
                    onClick={() => {
                      setFlagForManualReviewDialogOpen(true);
                    }}
                  >
                    <span className="ml-2">Flag for Manual Review</span>
                  </Button>
                ) : null}
                {canRemoveManualReviewFlag ? (
                  <Button
                    className="flex w-full justify-start border-0 bg-none outline-none"
                    variant="ghost"
                    onClick={() => {
                      setRemoveManualReviewFlagDialogOpen(true);
                    }}
                  >
                    <span className="ml-2">Remove Manual Review Flag</span>
                  </Button>
                ) : null}
                {canGoToParentAccount ? (
                  <Button
                    className="flex w-full justify-start border-0 bg-none outline-none"
                    variant="ghost"
                    onClick={() => {
                      window.location.href = `/policy/${policy?.parentPolicyId}/policy-details`;
                    }}
                  >
                    <span className="ml-2">Go to Prior Policy</span>
                  </Button>
                ) : null}
                {canGoToRenewalAccount ? (
                  <Button
                    className="flex w-full justify-start border-0 bg-none outline-none"
                    variant="ghost"
                    onClick={() => {
                      window.location.href = `/policy/${renewalPolicyData?.policyId}/policy-details`;
                    }}
                  >
                    <span className="ml-2">Go to Renewal Account</span>
                  </Button>
                ) : null}
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
        <DialogContent className={dialogClassName}>
          {dialogContent === 'RATING' ? (
            <DebugTransaction
              accountId={id!}
              setLoadingDebug={setLoadingDebug}
            />
          ) : null}
          {dialogContent === 'TRANSFER' ? <Transfer accountId={id!} /> : null}
          {dialogContent === 'REFER' ? <Refer accountId={id!} /> : null}
          {dialogContent === 'DECLINE' ? (
            <Decline title="Decline" accountId={id!} />
          ) : null}

          {dialogContent === 'NOT_TAKEN' ? (
            <Decline title="Not Taken" accountId={id!} />
          ) : null}

          {dialogContent === 'DISCARD' ? <Discard accountId={id!} /> : null}

          {dialogContent === 'REOPEN' ? <Reopen accountId={id!} /> : null}
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog to Close Renewal */}
      <CloseRenewal
        renewalPolicyData={renewalPolicyData}
        isOpen={closeRenewalDialogOpen}
        accountId={id!}
        onOpenChange={setCloseRenewalDialogOpen}
        setMessage={setMessage}
      />

      {/* Confirmation dialog to Mark as Non Renew */}
      <MarkAsNonRenew
        isOpen={markAsNonRenewDialogOpen}
        accountId={id!}
        onOpenChange={setMarkAsNonRenewDialogOpen}
        setMessage={setMessage}
      />

      {/* Copy Quote dialog (DC-3396) */}
      <CopyQuote
        isOpen={copyQuoteDialogOpen}
        accountId={id!}
        onOpenChange={setCopyQuoteDialogOpen}
        policy={policy}
        setMessage={setMessage}
      />

      {/* Confirmation dialog to Flag for Manual Review */}
      <FlagForManualRenew
        isOpen={flagForManualReviewDialogOpen}
        accountId={id!}
        onOpenChange={setFlagForManualReviewDialogOpen}
        setMessage={setMessage}
      />

      {/* Confirmation dialog to Mark as Renewable */}
      <MarkAsRenewable
        isOpen={markAsRenewableDialogOpen}
        accountId={id!}
        onOpenChange={setMarkAsRenewableDialogOpen}
        setMessage={setMessage}
      />

      {/* Confirmation dialog to delete Manual Review */}
      <RemoveManualReviewFlag
        isOpen={removeManualReviewFlagDialogOpen}
        accountId={id!}
        onOpenChange={setRemoveManualReviewFlagDialogOpen}
        setMessage={setMessage}
      />

      {/* Confirmation dialog to reopen account */}
      <CancelPolicyDialog
        isOpen={cancelPolicyDialogOpen}
        onOpenChange={setCancelPolicyDialogOpen}
        setMessage={setMessage}
        policy={policy}
      />
      {/* Confirmation dialog to reopen account */}
      <ReopenUnlockedAccountDialog
        isOpen={reopenDialogOpen}
        onOpenChange={setReopenDialogOpen}
        setMessage={setMessage}
        policyQK={policyQK}
        policyId={id!}
      />
      {/* Confirmation dialog to lock policy */}
      <LockPolicyDialog
        isOpen={lockPolicyDialogOpen}
        onOpenChange={setLockPolicyDialogOpen}
        isLoading={loading}
        setLoading={setLoading}
        setMessage={setMessage}
        policyId={id!}
      />

      {/* Agent Authority confirmation dialog */}
      <AgentAuthorityConfirmDialog
        isOpen={agentAuthorityConfirmDialogOpen}
        onOpenChange={setAgentAuthorityConfirmDialogOpen}
        onConfirm={() => setIssuePolicyDialogOpen(true)}
        policyId={id!}
      />

      {/* Confirmation dialog to issue policy */}
      <IssuePolicyDialog
        isOpen={issuePolicyDialogOpen}
        onOpenChange={setIssuePolicyDialogOpen}
        isIssuing={issuing}
        setIssuing={setIssuing}
        setMessage={setMessage}
        setLoading={setLoading}
        warehouseKey={warehouseKey}
        policyQK={policyQK}
        formulasQK={formulasQK}
        policyId={id!}
        permissionsQK={permissionsQK}
      />

      <BypassValidationDialog
        isOpen={bypassValidationDialogOpen}
        onOpenChange={setBypassValidationDialogOpen}
        isBypassing={bypassing}
        bypassed={policy?.bypassValidators}
        setBypassing={setBypassing}
        setMessage={setMessage}
        setLoading={setLoading}
        policyQK={policyQK}
        formulasQK={formulasQK}
        policyId={id!}
        permissionsQK={permissionsQK}
      />
    </>
  );
};
